<template>
  <div class="table-header">
    <transition-group
      :name="!isResizing ? 'column-move' : ''"
      tag="div"
      class="header-row"
    >
      <div
        v-for="(column, index) in columns"
        :key="column.field"
        class="column-head"
        :style="columnStyles[column.field]"
        :draggable="!isLocked(index)"
        @dragstart="onDragStart($event, column)"
        @dragenter="onDragEnter(column)"
        @dragover.prevent
        @dragend="onDragEnd"
      >
        <div v-if="column.field === 'hidden'" class="checkbox-header">
          <div class="checkbox-container" @click="toggleSelectAll">
            <div
              class="checkbox-ui"
              :class="{
                checked: isAllSelected,
                indeterminate: isIndeterminate,
              }"
            >
              <span class="checkmark"></span>
            </div>
          </div>
        </div>
        <template v-else>
          <span class="label">
            {{ columnNames[column.field] }}
          </span>
        </template>
        <span
          v-if="isSortable(column)"
          class="header-container-icon"
          @click="onChangeSort(column.field)"
        >
          <span v-if="!columnSortByKey(column.field)" key="ico-sort">
            <ico name="icoSort" size="16" />
          </span>
          <span
            v-else-if="columnSortByKey(column.field).sortType === 'DESC'"
            key="ico-arrow-downward"
          >
            <ico name="icoArrowDownward" size="16" color="#79919c" />
          </span>
          <span v-else key="ico-arrow-upward">
            <ico name="icoArrowUpward" size="16" color="#79919c" />
          </span>
        </span>
        <span class="resizer" @mousedown="resize($event, column)" />
      </div>
    </transition-group>
  </div>
</template>

<script>
import { columnNames } from "../../utils/table/columnNames";
import { mapActions, mapGetters } from "vuex";
import { eventBus } from "@/main";
import CELL_COMPONENTS from "../../utils/tableCellRegistry";

export default {
  name: "PurchasesTreeTableHeader",
  inject: ["tableContext"],

  props: {
    columns: {
      type: Array,
      required: true,
    },
    widths: {
      type: Object,
      required: true,
    },
    isResizing: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      columnSortingPaths: {
        proposalNumber: "proposal.number",
        expirationDays: "proposal.endDate",
        startDate: "proposal.startDate",
        endDate: "proposal.endDate",
        price: "price",
        lotsNumber: "proposal.numberLots",
      },
      draggedColumn: null,
      isDraggingColumn: false,
      dragOverTarget: null,
      dragOverTimer: null,
    };
  },

  computed: {
    ...mapGetters("columnsSort", ["columnSortByKey"]),
    columnNames() {
      return columnNames;
    },
    isAllSelected() {
      return this.tableContext.isAllSelected;
    },
    isIndeterminate() {
      return this.tableContext.isIndeterminate;
    },
    columnStyles() {
      const styles = {};

      for (const column of this.columns) {
        const width = CELL_COMPONENTS[column.field]?.width
          ? CELL_COMPONENTS[column.field].width
          : this.widths[column.field] || column.width;

        styles[column.field] = {
          flex: `0 0 ${width}px`,
          width: `${width}px`,
        };
      }

      return styles;
    },
  },

  methods: {
    ...mapActions("columnsSort", ["setColumnSort", "removeColumnSort"]),
    resize(e, column) {
      this.$emit("resize", e, column);
    },
    isSortable(column) {
      const sortableColumns = [
        "proposalNumber",
        "expirationDays",
        "startDate",
        "endDate",
        "price",
        "lotsNumber",
      ];
      return sortableColumns.includes(column.field);
    },
    onChangeSort(field) {
      const columnSort = this.columnSortByKey(field);

      if (columnSort) {
        if (columnSort.sortType === "DESC") {
          this.setColumnSort({
            fieldPath: field,
            sortingPath: this.columnSortingPaths[field],
            sortType: "ASC",
          });
        } else {
          this.removeColumnSort(field);
        }
      } else {
        this.setColumnSort({
          fieldPath: field,
          sortingPath: this.columnSortingPaths[field],
          sortType: "DESC",
        });
      }
    },
    toggleSelectAll() {
      this.tableContext.toggleSelectAll();
    },
    onDragStart(event, column) {
      if (this.isResizing) {
        event.preventDefault();
        return;
      }
      this.draggedColumn = column;
      this.isDraggingColumn = true;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", column.field);
    },

    onDragEnter(targetColumn) {
      if (!this.draggedColumn) return;
      if (this.draggedColumn.field === targetColumn.field) return;
      if (targetColumn.index < 3) return;

      this.dragOverTarget = targetColumn;

      if (this.dragOverTimer) clearTimeout(this.dragOverTimer);

      this.dragOverTimer = setTimeout(() => {
        if (!this.dragOverTarget || !this.draggedColumn) return;
        eventBus.$emit(
          "moveColumnByIndex",
          this.draggedColumn.index,
          this.dragOverTarget.index,
        );

        this.draggedColumn = this.dragOverTarget;

        this.dragOverTimer = null;
      }, 150);
    },

    onDragEnd() {
      this.draggedColumn = null;
      this.isDraggingColumn = false;
    },
    isLocked(index) {
      return index < 2;
    },
  },
};
</script>

<style scoped lang="less">
.table-header {
  position: sticky;
  top: 0;
  z-index: 10;
  .column-move-move {
    transition: transform 150ms ease;
  }
}

.header-row {
  display: flex;
  min-width: max-content;
  max-width: max-content;
}

.column-head {
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;

  height: 48px;
  padding: 0 12px;

  font-family: Roboto, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgb(121, 145, 156);

  box-sizing: border-box;
  overflow: hidden;
  background: white;
  .checkbox-container {
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: calc(14px + 18px);

    .checkbox-ui {
      width: 17px;
      height: 17px;
      border: 2px solid #cfd8dd;
      border-radius: 2px;
      position: relative;
      background-color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;

      .checkmark {
        opacity: 0;
      }

      &.checked {
        background-color: #37474f;
        border-color: #37474f;

        .checkmark {
          position: absolute;
          width: 12px;
          height: 7px;
          border-left: 2px solid white;
          border-bottom: 2px solid white;
          transform: rotate(-45deg);
          bottom: 5px;
          opacity: 1;
          transition: opacity 0.2s ease;
        }
      }
      &.indeterminate {
        background-color: #37474f;
        border-color: #37474f;
        .checkmark {
          position: absolute;
          width: 10px;
          bottom: 5.5px;
          border-bottom: 2px solid white;
          opacity: 1;
        }
      }
    }
  }
  .header-container-icon {
    cursor: pointer;
  }
  .header-container-icon ::v-deep svg path {
    fill: #79919c !important;
  }
}

.column-head:not(:nth-child(1)):not(:nth-child(2)) {
  cursor: move;
}

.column-head:nth-child(1) {
  position: sticky;
  left: 0;
  z-index: 3;
  background: white;
}

.column-head:nth-child(2) {
  position: sticky;
  left: calc(52px + 14px + 14px);
  z-index: 3;
  background: white;
}

.column-head:nth-child(-n + 2) .resizer {
  display: none;
}

.column-head:nth-child(-n + 2) {
  padding: 0;
}
.label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resizer {
  position: absolute;
  right: 0;
  top: 0;

  width: 8px;
  height: 100%;

  cursor: ew-resize;

  &::before {
    content: "";
    position: absolute;
    top: 16px;
    right: 0;

    width: 1px;
    height: 16px;

    background-color: #cfd8dd;
  }
}
</style>
